import { type RecordId } from '../types';
import DataStore from './DataStore';
import DataStoreRecordBehaviors from './DataStoreRecordBehaviors';
import {
  type CardLayoutConfigurationData,
  recordLayoutKey,
} from '../cardLayouts';

const CONFIGURATION_RECORD_ID: RecordId = '7254702382411211748';
const CARD_LAYOUT_RECORD_ID: RecordId = '7327131383223981986';

abstract class BaseConfigurationRecord extends DataStoreRecordBehaviors(
  Object
) {
  constructor(data = {}) {
    super();

    for (const key in data) {
      this[key] = data[key];
    }
  }
}

class ConfigurationRecord extends BaseConfigurationRecord {
  serializedKeys = [];

  uniqueId() {
    return CONFIGURATION_RECORD_ID;
  }
}

class CardLayoutConfiguration extends BaseConfigurationRecord {
  serializedKeys = process.env.TEST
    ? []
    : // @ts-expect-error The enum type is wrong, this is actually an array
      window.aha?.enums.CardLayoutRecordTypesEnum.values.map(recordType =>
        recordLayoutKey(recordType)
      ) || [];

  uniqueId(): RecordId {
    return CARD_LAYOUT_RECORD_ID;
  }

  saveCardLayout(cardLayout: CardLayoutConfigurationData) {
    this.serializedKeys.forEach(key => {
      if (key in cardLayout) {
        this.set(key, cardLayout[key]);
      }
    });
  }
}

const ConfigurationClasses = {
  [CONFIGURATION_RECORD_ID]: ConfigurationRecord,
  [CARD_LAYOUT_RECORD_ID]: CardLayoutConfiguration,
};

// Data store for global configuration that applies to the entire whiteboard.
class DataStoreGlobal extends DataStore<BaseConfigurationRecord> {
  static fromJSON(data: Partial<BaseConfigurationRecord>) {
    if (!data.id) {
      console.error('No id provided for configuration object.', data);
      return {};
    }

    const RecordClass = ConfigurationClasses[data.id];
    if (!RecordClass) {
      console.error('Invalid configuration ID.');
      return {};
    }

    return new RecordClass(data);
  }

  get configuration(): ConfigurationRecord {
    return this.loadConfigRecord(
      CONFIGURATION_RECORD_ID
    ) as ConfigurationRecord;
  }

  get cardLayout(): CardLayoutConfiguration {
    return this.loadConfigRecord(
      CARD_LAYOUT_RECORD_ID
    ) as CardLayoutConfiguration;
  }

  loadConfigRecord(id: RecordId): BaseConfigurationRecord | undefined {
    let record = this.findRecordById(id);

    if (!record) {
      // We won't create any new records while initially laoding the doc
      if (this._client?.processingRemoteOps) {
        return;
      }

      const RecordClass = ConfigurationClasses[id];
      record = this.addRecord(new RecordClass());
    }

    return record;
  }
}

export { ConfigurationRecord };
export default DataStoreGlobal;
